<template>
  <VaDropdown :offset="[13, 0]" class="notification-dropdown" stick-to-edges :close-on-content-click="false">
    <template #anchor>
      <VaButton
        preset="secondary"
        color="textPrimary"
        aria-label="查看消息提醒"
        :loading="loading"
        @click="loadNotifications"
      >
        <VaBadge v-if="unreadCount" overlap :text="unreadCount > 9 ? '9+' : String(unreadCount)" color="danger">
          <VaIconNotification class="notification-dropdown__icon" />
        </VaBadge>
        <VaIconNotification v-else class="notification-dropdown__icon" />
      </VaButton>
    </template>

    <VaDropdownContent class="h-full sm:h-auto sm:max-w-[420px]">
      <section class="max-h-[70vh] overflow-auto p-4 sm:max-h-[360px]">
        <div class="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 class="text-sm font-semibold text-gray-800">消息提醒</h2>
            <p class="mt-1 text-xs text-gray-500">{{ subtitle }}</p>
          </div>
          <div class="flex items-center gap-1">
            <VaButton
              v-if="activeNotifications.length"
              size="small"
              preset="plain"
              aria-label="全部标为已读"
              @click="markAllAsRead"
            >
              全部已读
            </VaButton>
            <VaButton size="small" preset="plain" icon="refresh" aria-label="刷新消息提醒" @click="loadNotifications" />
          </div>
        </div>

        <div v-if="loading" class="space-y-2">
          <div v-for="i in 3" :key="i" class="h-14 rounded bg-gray-100 animate-pulse" />
        </div>

        <VaList v-else-if="visibleNotifications.length" class="mb-3 space-y-1">
          <VaListItem
            v-for="item in visibleNotifications"
            :key="item.id"
            class="rounded cursor-pointer hover:bg-gray-50"
            @click="openNotification(item)"
          >
            <VaListItemSection icon class="mx-0 p-0">
              <span class="flex h-9 w-9 items-center justify-center rounded bg-blue-50">
                <VaIcon :name="item.icon" :color="item.color" size="18px" />
              </span>
            </VaListItemSection>
            <VaListItemSection>
              <p class="text-sm font-medium text-gray-800">{{ item.title }}</p>
              <p class="mt-1 text-xs leading-relaxed text-gray-500">{{ item.message }}</p>
            </VaListItemSection>
            <VaListItemSection icon class="mx-1 text-xs text-gray-400">
              {{ item.timeLabel }}
            </VaListItemSection>
          </VaListItem>
        </VaList>

        <div v-else class="rounded border border-dashed border-gray-200 px-4 py-8 text-center">
          <VaIcon name="notifications_none" color="secondary" size="28px" />
          <p class="mt-2 text-sm font-medium text-gray-700">暂无待处理提醒</p>
          <p class="mt-1 text-xs text-gray-500">有新的评分、复核或练习建议时会出现在这里。</p>
        </div>

        <VaAlert v-if="error" color="warning" class="mb-3 text-xs">{{ error }}</VaAlert>

        <VaButton
          v-if="activeNotifications.length > baseNumberOfVisibleNotifications"
          preset="primary"
          class="w-full"
          @click="displayAllNotifications = !displayAllNotifications"
        >
          {{ displayAllNotifications ? '收起提醒' : '查看全部提醒' }}
        </VaButton>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import VaIconNotification from '../../../icons/VaIconNotification.vue'

const router = useRouter()
const auth = useAuthStore()

const baseNumberOfVisibleNotifications = 4
const displayAllNotifications = ref(false)
const loading = ref(false)
const error = ref('')
const notifications = ref<AppNotification[]>([])
const readNotificationIds = ref<string[]>([])

interface AppNotification {
  id: string
  title: string
  message: string
  icon: string
  color: string
  timeLabel: string
  route: RouteLocationRaw
}

const activeNotifications = computed(() =>
  notifications.value.filter((item) => !readNotificationIds.value.includes(item.id)),
)
const unreadCount = computed(() => activeNotifications.value.length)
const subtitle = computed(() =>
  unreadCount.value ? `${unreadCount.value} 个提醒需要处理` : '当前没有紧急事项，保持节奏就好。',
)
const visibleNotifications = computed(() =>
  displayAllNotifications.value
    ? activeNotifications.value
    : activeNotifications.value.slice(0, baseNumberOfVisibleNotifications),
)

const readStorageKey = computed(() => (auth.user ? `ylm_read_notifications_${auth.user.id}` : ''))

function setPartialError(modules: { name: string; result: PromiseSettledResult<unknown> }[]) {
  const failedModules = modules.filter((item) => item.result.status === 'rejected').map((item) => item.name)
  if (failedModules.length) {
    error.value = `${failedModules.join('、')}提醒暂时不可用。`
  }
}

async function loadNotifications() {
  if (!auth.user) return

  loading.value = true
  error.value = ''
  try {
    loadReadNotificationIds()
    notifications.value =
      auth.user.role === 'student' ? await loadStudentNotifications() : await loadTeacherNotifications()
  } catch {
    error.value = '消息提醒加载失败，请稍后重试。'
    notifications.value = []
  } finally {
    loading.value = false
  }
}

async function loadStudentNotifications(): Promise<AppNotification[]> {
  const [pendingRes, recommendationRes] = await Promise.allSettled([
    api.get('/submissions/my', { params: { status: 'submitted', pageSize: 1 } }),
    api.get('/statistics/recommendations'),
  ])

  const items: AppNotification[] = []
  const pendingCount = pendingRes.status === 'fulfilled' ? pendingRes.value.data.total || 0 : 0
  const recommended = recommendationRes.status === 'fulfilled' ? recommendationRes.value.data.items?.[0] : null

  setPartialError([
    { name: '待评分', result: pendingRes },
    { name: '下一题建议', result: recommendationRes },
  ])

  if (pendingCount > 0) {
    items.push({
      id: `student-pending-submissions-${pendingCount}`,
      title: `${pendingCount} 份练习等待评分`,
      message: '评分完成后会生成报告和下一步行动计划。',
      icon: 'schedule',
      color: 'warning',
      timeLabel: '待处理',
      route: { name: 'history' },
    })
  }

  if (recommended) {
    items.push({
      id: `student-recommendation-${recommended.id}`,
      title: '已为你准备下一题',
      message: `${recommendationRes.status === 'fulfilled' ? recommendationRes.value.data.reason || '根据最近表现推荐' : '根据最近表现推荐'}：${recommended.title}`,
      icon: 'flag',
      color: 'info',
      timeLabel: '现在',
      route: { name: 'practice', params: { id: recommended.id } },
    })
  }

  return items
}

async function loadTeacherNotifications(): Promise<AppNotification[]> {
  const [pendingRes, qualityRes] = await Promise.allSettled([
    api.get('/submissions/all', { params: { status: 'submitted', pageSize: 1 } }),
    api.get('/statistics/teacher/quality', { params: { limit: 100 } }),
  ])

  const items: AppNotification[] = []
  const pendingCount = pendingRes.status === 'fulfilled' ? pendingRes.value.data.total || 0 : 0
  const issueCount = qualityRes.status === 'fulfilled' ? qualityRes.value.data.issueCount || 0 : 0

  setPartialError([
    { name: '待批改', result: pendingRes },
    { name: '评分质量', result: qualityRes },
  ])

  if (pendingCount > 0) {
    items.push({
      id: `teacher-pending-submissions-${pendingCount}`,
      title: `${pendingCount} 份提交待批改`,
      message: '进入批改审阅页处理学生提交。',
      icon: 'rate_review',
      color: 'warning',
      timeLabel: '待处理',
      route: { name: 'teacher-reviews' },
    })
  }

  if (issueCount > 0) {
    items.push({
      id: `teacher-quality-issues-${issueCount}`,
      title: `${issueCount} 条评分需复核`,
      message: `最近 ${qualityRes.status === 'fulfilled' ? qualityRes.value.data.scanned || 0 : 0} 份评分中发现分数/扣分不一致。`,
      icon: 'error_outline',
      color: 'danger',
      timeLabel: '需确认',
      route: { name: 'teacher-reviews' },
    })
  }

  return items
}

function openNotification(item: AppNotification) {
  markAsRead(item.id)
  router.push(item.route)
}

function loadReadNotificationIds() {
  if (!readStorageKey.value) return

  try {
    const stored = JSON.parse(localStorage.getItem(readStorageKey.value) || '[]')
    readNotificationIds.value = Array.isArray(stored) ? stored.filter((item) => typeof item === 'string') : []
  } catch {
    readNotificationIds.value = []
  }
}

function persistReadNotificationIds() {
  if (!readStorageKey.value) return
  localStorage.setItem(readStorageKey.value, JSON.stringify(readNotificationIds.value.slice(-100)))
}

function markAsRead(id: string) {
  if (readNotificationIds.value.includes(id)) return
  readNotificationIds.value = [...readNotificationIds.value, id]
  persistReadNotificationIds()
}

function markAllAsRead() {
  const nextIds = new Set(readNotificationIds.value)
  for (const item of activeNotifications.value) {
    nextIds.add(item.id)
  }
  readNotificationIds.value = [...nextIds]
  persistReadNotificationIds()
}

onMounted(loadNotifications)

watch(
  () => auth.user?.role,
  () => {
    loadNotifications()
  },
)
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}
</style>
